/**
 * Shared state of the persistent page shell.
 *
 * Every flag is mirrored onto <html> as an is-* class, so CSS reacts to state
 * without the JS knowing about styling. <html> is the right place because Swup
 * (via the body class plugin) rewrites the body classes on every visit, while
 * the head plugin only touches the lang/dir attributes of <html>.
 */
const HTML_CLASSES = {
  easyMode: 'is-easy-mode',
  soundOn: 'is-sound-on',
  gamePlaying: 'is-game-playing',
  chatOpen: 'is-chat-open',
  navOpen: 'is-nav-open',
}

const state = {
  // Already decided by the inline script in header.php, before first paint.
  easyMode: document.documentElement.classList.contains('is-easy-mode'),
  soundOn: false,
  gamePlaying: false,
  chatOpen: false,
  navOpen: false,
}

const listeners = new Set()

export const getState = () => ({ ...state })

export function setState(patch) {
  const changed = Object.keys(patch).filter((key) => state[key] !== patch[key])
  if (!changed.length) return

  Object.assign(state, patch)
  changed.forEach((key) => {
    if (HTML_CLASSES[key]) {
      document.documentElement.classList.toggle(HTML_CLASSES[key], Boolean(state[key]))
    }
  })

  const snapshot = getState()
  listeners.forEach((listener) => listener(snapshot, changed))
}

/** Listen to state changes; returns an unsubscribe function. */
export function subscribe(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Fire a shell event, e.g. emit('play') → document 'danino:play'. */
export function emit(name, detail = {}) {
  document.dispatchEvent(new CustomEvent(`danino:${name}`, { detail }))
}

/** Listen to a shell event; returns an unsubscribe function. */
export function on(name, handler) {
  const type = `danino:${name}`
  document.addEventListener(type, handler)
  return () => document.removeEventListener(type, handler)
}

/** localStorage wrapped for private mode / blocked storage. */
export const storage = {
  get(key) {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value)
    } catch {
      // Storage unavailable — the choice just isn't remembered.
    }
  },
}
